import React, { useState, useEffect } from 'react';
import { createAffiliate, getAllAffiliates, updateAffiliateStatus } from '../../services/affiliate';
import { markCommissionAsPaid, getAffiliateCommissions } from '../../services/affiliate';
import '../../styles/affiliate.css';

const AffiliateManagement = () => {
  const [affiliates, setAffiliates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [selectedAffiliate, setSelectedAffiliate] = useState(null);
  const [commissions, setCommissions] = useState([]);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    userId: '',
    code: ''
  });

  useEffect(() => {
    loadAffiliates();
  }, []);

  const loadAffiliates = async () => {
    try {
      const affiliatesData = await getAllAffiliates();
      setAffiliates(affiliatesData);
    } catch (error) {
      console.error('Error loading affiliates:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleCreateAffiliate = async (e) => {
    e.preventDefault();
    try {
      await createAffiliate(formData);
      setFormData({ name: '', email: '', phone: '', userId: '', code: '' });
      setShowCreateForm(false);
      loadAffiliates();
    } catch (error) {
      console.error('Error creating affiliate:', error);
    }
  };

  const handleToggleStatus = async (affiliateId, currentStatus) => {
    try {
      await updateAffiliateStatus(affiliateId, !currentStatus);
      loadAffiliates();
    } catch (error) {
      console.error('Error updating affiliate status:', error);
    }
  };

  const handleViewCommissions = async (affiliateId) => {
    console.log('View commissions clicked for affiliate:', affiliateId);
    try {
      const commissionsData = await getAffiliateCommissions(affiliateId);
      console.log('Commissions data:', commissionsData);
      setCommissions(commissionsData);
      setSelectedAffiliate(affiliateId);
      console.log('Modal should now be visible');
    } catch (error) {
      console.error('Error loading commissions:', error);
    }
  };

  const handleMarkCommissionPaid = async (commissionId) => {
    try {
      await markCommissionAsPaid(commissionId);
      // Refresh commissions
      if (selectedAffiliate) {
        handleViewCommissions(selectedAffiliate);
      }
    } catch (error) {
      console.error('Error marking commission as paid:', error);
    }
  };

  if (loading) {
    return (
      <div className="affiliate-management">
        <div className="loading-container">
          <div className="loading-spinner">Loading affiliates...</div>
        </div>
      </div>
    );
  }

  return (
    <div className="affiliate-management">
      <div className="page-header">
        <h1>Affiliate Management</h1>
        <button 
          className="btn btn-primary"
          onClick={() => setShowCreateForm(true)}
        >
          Create Affiliate
        </button>
      </div>

      {/* Create Affiliate Form */}
      {showCreateForm && (
        <div className="modal-overlay">
          <div className="modal">
            <div className="modal-header">
              <h2>Create New Affiliate</h2>
              <button 
                className="close-button"
                onClick={() => setShowCreateForm(false)}
              >
                ×
              </button>
            </div>
            
            <form onSubmit={handleCreateAffiliate} className="affiliate-form">
              <div className="form-group">
                <label>Name</label>
                <input
                  type="text"
                  value={formData.name}
                  onChange={(e) => setFormData({...formData, name: e.target.value})}
                  required
                />
              </div>
              
              <div className="form-group">
                <label>Email</label>
                <input
                  type="email"
                  value={formData.email}
                  onChange={(e) => setFormData({...formData, email: e.target.value})}
                  required
                />
              </div>
              
              <div className="form-group">
                <label>Phone</label>
                <input
                  type="tel"
                  value={formData.phone}
                  onChange={(e) => setFormData({...formData, phone: e.target.value})}
                />
              </div>
              
              <div className="form-group">
                <label>User ID</label>
                <input
                  type="text"
                  value={formData.userId}
                  onChange={(e) => setFormData({...formData, userId: e.target.value})}
                  required
                />
              </div>
              
              <div className="form-group">
                <label>Affiliate Code</label>
                <input
                  type="text"
                  value={formData.code}
                  onChange={(e) => setFormData({...formData, code: e.target.value.toUpperCase()})}
                  placeholder="e.g., STUDENT20"
                  required
                />
              </div>
              
              <div className="form-actions">
                <button type="submit" className="btn btn-primary">
                  Create Affiliate
                </button>
                <button 
                  type="button" 
                  className="btn btn-secondary"
                  onClick={() => setShowCreateForm(false)}
                >
                  Cancel
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Affiliates List */}
      <div className="affiliates-list">
        <h2>All Affiliates</h2>
        
        {affiliates.length === 0 ? (
          <div className="no-affiliates">
            <p>No affiliates created yet.</p>
          </div>
        ) : (
          <div className="affiliates-grid">
            {affiliates.map((affiliate) => (
              <div key={affiliate.id} className="affiliate-card">
                <div className="affiliate-info">
                  <h3>{affiliate.name}</h3>
                  <p>{affiliate.email}</p>
                  <p>Code: <strong>{affiliate.code}</strong></p>
                  <p>Referrals: {affiliate.totalReferrals || 0}</p>
                  <p>Earnings: K{(affiliate.totalEarnings || 0).toLocaleString()}</p>
                </div>
                
                <div className="affiliate-actions">
                  <div className={`status ${affiliate.isActive ? 'active' : 'inactive'}`}>
                    {affiliate.isActive ? 'Active' : 'Inactive'}
                  </div>
                  
                  <button 
                    className="btn btn-sm btn-secondary"
                    onClick={() => handleToggleStatus(affiliate.id, affiliate.isActive)}
                  >
                    {affiliate.isActive ? 'Deactivate' : 'Activate'}
                  </button>
                  
                  <button 
                    className="btn btn-sm btn-primary"
                    onClick={() => handleViewCommissions(affiliate.id)}
                  >
                    View Commissions
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Commissions Modal */}
      {selectedAffiliate && (
        <>
          {console.log('Modal rendering for affiliate:', selectedAffiliate)}
          <div className="modal-overlay">
            <div className="modal large">
              <div className="modal-header">
                <h2>Commission History</h2>
                <button 
                  className="close-button"
                  onClick={() => {
                    console.log('Close button clicked');
                    setSelectedAffiliate(null);
                  }}
                >
                  ×
                </button>
              </div>
              
              <div className="commissions-list">
                {commissions.length === 0 ? (
                  <p>No commissions found.</p>
                ) : (
                  commissions.map((commission) => (
                    <div key={commission.id} className="commission-item">
                      <div className="commission-info">
                        <h4>Payment ID: {commission.paymentId}</h4>
                        <p>User ID: {commission.userId}</p>
                        <p>Original: K{commission.originalAmount.toLocaleString()}</p>
                        <p>Discount: K{commission.discountAmount.toLocaleString()}</p>
                        <p>Final: K{commission.finalAmount.toLocaleString()}</p>
                        <p>Date: {new Date(commission.createdAt).toLocaleDateString()}</p>
                      </div>
                      
                      <div className="commission-amount">
                        <div className="amount">K{commission.commissionAmount.toLocaleString()}</div>
                        <div className={`status ${commission.status}`}>
                          {commission.status}
                        </div>
                        
                        {commission.status === 'pending' && (
                          <button 
                            className="btn btn-sm btn-success"
                            onClick={() => handleMarkCommissionPaid(commission.id)}
                          >
                            Mark as Paid
                          </button>
                        )}
                      </div>
                    </div>
                  ))
                )}
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default AffiliateManagement;
